import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { TabsTrigger } from "~/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { Text } from "~/components/text";
import { MAX_KEYWORDS_CHARACTERS_COUNT } from "~/shared/constants";

interface PhrasesTabProps {
  value: "phrases";
  testId: string;
  onAdd: () => void;
  addDisabled: boolean;
  disabled?: never;
  characterCount: number;
}

interface SingleWordsTabProps {
  value: "singleWords";
  testId: string;
  disabled: boolean;
  characterCount: number;
  onAdd?: never;
  addDisabled?: never;
}

type FinalListTabTriggerProps = PhrasesTabProps | SingleWordsTabProps;

const TAB_TRIGGER_CLASSES = `group border border-transparent rounded-none rounded-t-lg h-[48px] border-b-primary-200
  data-[state=active]:text-foreground
  data-[state=active]:bg-primary-50
  data-[state=active]:border-primary-200
  data-[state=active]:border-b-transparent
  data-[state=active]:z-10
  group-data-[variant=default]/tabs-list:data-[state=active]:shadow-none
  hover:bg-primary-50/50 cursor-pointer w-full`;

export const FinalListTabTrigger = ({
  value,
  testId,
  onAdd,
  addDisabled,
  disabled,
  characterCount,
}: FinalListTabTriggerProps) => {
  const { t } = useTranslation(["components"]);

  if (value === "phrases") {
    return (
      <TabsTrigger
        value="phrases"
        className={TAB_TRIGGER_CLASSES}
        data-testid={testId}
        asChild
      >
        <div className="flex items-baseline gap-4">
          <Text as="span" variant="body">
            {t("components:keywordsTab.finalKeywords.tabs.phrases")}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="ml-2 px-2 rounded-full bg-primary-100/50 group-data-[state=active]:bg-primary-100">
                  <Text
                    as="span"
                    variant="accent"
                    className="opacity-50 group-data-[state=active]:opacity-100"
                  >
                    {characterCount}/{MAX_KEYWORDS_CHARACTERS_COUNT}
                  </Text>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t("components:keywordsTab.finalKeywords.characterCount")}
              </TooltipContent>
            </Tooltip>
          </Text>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
            disabled={addDisabled}
            data-testid="add-keyword-button"
            className="text-primary-500 hover:text-primary-600 disabled:opacity-50"
          >
            <Plus size={16} />
          </button>
        </div>
      </TabsTrigger>
    );
  }

  const trigger = (
    <TabsTrigger
      value="singleWords"
      disabled={disabled}
      className={TAB_TRIGGER_CLASSES}
      data-testid={testId}
      asChild
    >
      <div className="flex items-baseline gap-4">
        {t("components:keywordsTab.finalKeywords.tabs.singleWords")}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="px-2 rounded-full bg-primary-100/50 group-data-[state=active]:bg-primary-100">
              <Text
                as="span"
                variant="accent"
                className="opacity-50 group-data-[state=active]:opacity-100"
              >
                {characterCount}/{MAX_KEYWORDS_CHARACTERS_COUNT}
              </Text>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {t("components:keywordsTab.finalKeywords.characterCount")}
          </TooltipContent>
        </Tooltip>
      </div>
    </TabsTrigger>
  );

  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex w-full">{trigger}</span>
        </TooltipTrigger>
        <TooltipContent>
          {t(
            "components:keywordsTab.finalKeywords.tabs.singleWordsDisabledTooltip",
          )}
        </TooltipContent>
      </Tooltip>
    );
  }

  return trigger;
};
